import { Button } from '@/components/ui/button';

interface SaveBarProps {
  readonly changes: number;
  /** Why the form cannot be saved yet, in the operator's words. */
  readonly issues: readonly string[];
  readonly isPending: boolean;
  readonly error?: string;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
}

/**
 * Appears when something has moved, and not before.
 *
 * A permanently visible Save is a button an operator learns to ignore; one that
 * arrives when they change something is a statement that there is unsaved work.
 * It says how many fields moved rather than just "unsaved changes", because
 * "3 changes" is checkable against what you think you did.
 *
 * Sticky to the bottom of the scroll container rather than fixed to the
 * viewport: it belongs to this form, and the console's drawer and modals have
 * to be able to sit over it.
 */
export function SaveBar({ changes, issues, isPending, error, onSave, onDiscard }: SaveBarProps) {
  if (changes === 0) return null;

  const blocked = issues.length > 0;

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-primary/30 bg-primary/10 px-5 py-4 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-medium text-foreground">
          {changes} {changes === 1 ? 'change' : 'changes'}, not saved
        </span>
        {blocked ? (
          <span className="text-xs text-err-fg">{issues.join(' ')}</span>
        ) : error === undefined ? null : (
          <span role="alert" className="text-xs text-err-fg">
            {error}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" onClick={onDiscard} disabled={isPending}>
          Discard
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={isPending || blocked}
          title={blocked ? issues.join(' ') : undefined}
        >
          {isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
